import React from 'react'
import { graphql } from 'gatsby'
import PropTypes from 'prop-types'
import { flatMap, kebabCase, uniqBy } from 'lodash'

import Layout from 'components/page/layouts/GatsbyLayout'
import Helmet from 'components/page/Helmet'
import ContactAnvil from 'components/Signup/ContactAnvil'
import Section from 'components/page/Section'
import ContactSupportForm from 'components/EmbedForm/ContactSupportForm'

import renderNavbar from 'helpers/renderNavbar'
import {
  getSocialCoverURL,
  getSeoTitle,
  getSeoDescription,
  getSocialTagLine,
} from 'helpers'

import useAnvilUser from 'hooks/useAnvilUser'

import BundleHeroSubSection from '../components/BundleHeroSubSection'
import FeatureContentSubSection from '../components/FeatureContentSubSection'
import BundleHeroFeaturesSubSection from '../components/BundleHeroFeaturesSubSection'

const BundleTemplate = ({
  location: { pathname },
  data: {
    sanityBundlePage: {
      seoData,
      features,
      name,
      description,
      coverImage,
      youtubeId,
    },
    allSanityFeaturePage,
    allMarkdownRemark,
    allSanityImageAsset,
  },
}) => {
  const { eid } = useAnvilUser() || {}
  const isUserLoggedInToAnvil = !!eid

  const allFaqs = flatMap(features.map((feature) => feature.faqs))
  const uniqueFaqs = uniqBy(allFaqs, ({ question }) => question)
  const allRelatedResources = flatMap(
    features.map((feature) => feature.relatedResources)
  )

  return (
    <Layout>
      <Helmet
        title={getSeoTitle({ seoData, title: name })}
        meta={{
          description: getSeoDescription({ seoData, description }),
          socialTagline: getSocialTagLine({ seoData, title: name }),
        }}
        pathname={pathname}
        socialImageURL={getSocialCoverURL('features/bundles', kebabCase(name))}
      />
      {renderNavbar()}
      <Section>
        <BundleHeroSubSection
          title={name}
          coverImage={coverImage}
          description={description}
          youtubeId={youtubeId}
          name={name}
        />
        <BundleHeroFeaturesSubSection
          features={features}
          hasCoverImage={!!coverImage}
        />
        <FeatureContentSubSection
          allFeatures={allSanityFeaturePage.nodes}
          markdowns={allMarkdownRemark.nodes}
          markdownCovers={allSanityImageAsset.nodes}
          relatedResources={allRelatedResources}
          faqs={uniqueFaqs}
        />
        <ContactAnvil
          scrollName="contact-support"
          renderAs="SubSection"
          hideSignup={isUserLoggedInToAnvil}
          title={isUserLoggedInToAnvil ? 'Contact support' : undefined}
          description={
            isUserLoggedInToAnvil
              ? `Curious about how ${name} works? Ask us anything, we're here to help.`
              : undefined
          }
          renderEmbedForm={
            isUserLoggedInToAnvil ? () => <ContactSupportForm /> : null
          }
        />
      </Section>
    </Layout>
  )
}

BundleTemplate.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  data: PropTypes.any,
  pageContext: PropTypes.shape({}),
}

export const pageQuery = graphql`
  query IndividualBundlePage(
    $id: String
    $products: [String]
    $slugs: [String]
    $coverTitles: [String]
  ) {
    sanityBundlePage(id: { eq: $id }) {
      id
      name
      description
      coverImage {
        asset {
          gatsbyImageData
          altText
        }
      }
      youtubeId
      seoData {
        metaTitle
        socialTitle
        metaDescription
      }
      features {
        id
        name
        description
        product
        primaryProduct
        marketingIcon {
          asset {
            gatsbyImageData
            altText
          }
        }
        coverImage {
          asset {
            gatsbyImageData
            altText
          }
        }
        pricingPlan
        newUntil
        docsLink {
          path
        }
        faqs {
          question
          _rawAnswer(resolveReferences: { maxDepth: 10 })
        }
        relatedResources {
          __typename
          ... on SanityFeature {
            name
            product
            primaryProduct
            coverImage {
              asset {
                gatsbyImageData
                altText
              }
            }
            description
          }
          __typename
          ... on SanityBlogPost {
            heroImage {
              asset {
                gatsbyImageData
              }
            }
            article {
              slug
              title
              primaryTag
              tags
              _rawSummary(resolveReferences: { maxDepth: 10 })
            }
          }
          __typename
          ... on SanityMarketingSiteLink {
            path
          }
          __typename
          ... on SanityYoutubeVideo {
            videoId
            title
            description
          }
        }
      }
    }
    allSanityFeaturePage(filter: { feature: { product: { in: $products } } }) {
      nodes {
        id
        feature {
          name
          description
          coverImage {
            asset {
              gatsbyImageData
              altText
            }
          }
          pricingPlan
          newUntil
          product
          primaryProduct
        }
      }
    }
    allMarkdownRemark(filter: { frontmatter: { slug: { in: $slugs } } }) {
      nodes {
        id
        frontmatter {
          title
          slug
          tags
        }
      }
    }
    allSanityImageAsset(filter: { title: { in: $coverTitles } }) {
      nodes {
        gatsbyImageData
        url
        title
      }
    }
  }
`

export default BundleTemplate
